

/** @enum DarkThemeMode kind of dark mode  */
export enum DarkThemeMode {
  DARK   = 'dark',
  DARK1  = 'dark1',
  DARK2  = 'dark2',
  DARK3  = 'dark3',
  DARK4  = 'dark4',
  DARK5  = 'dark5',
  NONE   = 'none'
}

/** @enum LightThemeMode kind of light mode  */
export enum LightThemeMode {
  LIGHT   = 'light',
  LIGHT1  = 'light1',
  LIGHT2  = 'light2',
  LIGHT3  = 'light3',
  LIGHT4  = 'light4',
  LIGHT5  = 'light5',
  NONE    = 'none'
}

/**
 *
 * @class Theme
 *
 * @brief the current styling theme for the component
 *
 * Color palettes:
 *
 *   https://palettes.shecodes.io/
 *   https://coolors.co/palettes/trending
 *   https://www.color-hex.com/color-palettes/
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      May 2023
 *
 */
export class Theme {

  /** the dark mode type (if it's a dark theme) */
  public darkMode: DarkThemeMode           = DarkThemeMode.NONE;

  /** the light mode type (if it's a light theme) */
  public lightMode: LightThemeMode         = LightThemeMode.LIGHT;

  /** background color for the wrapping <div> */
  public wrapper_background_color: string  = '#CCCCCC';

  /** foreground color for the wrapping <div> */
  public wrapper_foreground_color: string  = 'black';

  /** background color for the <canvas> */
  public canvas_background_color: string   = '#CCCCCC';

  /** foreground color for the <canvas> */
  public canvas_foreground_color: string   = 'black';

  /** the color of the background grid */
  public grid_color: string                = '#404040';

  /** spike line opacity */
  public spike_alpha: number               = 1.0;

  /** spike line color */
  public spike_color: string               = `#fd7201`;

  /** spike line width */
  public spike_width: number               = .5;

  /** height of the toolbar and its icons */
  public toolbar_height: number            = 24;

  /** standard constructor */
  constructor(init?: Partial<Theme>) {

    Object.assign(this, init);
  }

  /** build a theme instance by name */
  public static build(themeName: string): Theme|null {

    let darkModes: DarkThemeMode[] = [
      DarkThemeMode.DARK,
      DarkThemeMode.DARK1,
      DarkThemeMode.DARK2,
      DarkThemeMode.DARK3,
      DarkThemeMode.DARK4,
      DarkThemeMode.DARK5,
    ];

    let darkIndex: number  = darkModes.indexOf(themeName.toLowerCase().trim() as DarkThemeMode);

    if (darkIndex >= 0) {

      /* its a dark theme */

      return Theme.buildDarkTheme(darkModes[darkIndex]);
    }

    let lightModes: LightThemeMode[] = [
      LightThemeMode.LIGHT,
      LightThemeMode.LIGHT1,
      LightThemeMode.LIGHT2,
      LightThemeMode.LIGHT3,
      LightThemeMode.LIGHT4,
      LightThemeMode.LIGHT5,
    ];

    let lightIndex: number  = lightModes.indexOf(themeName.toLowerCase().trim() as LightThemeMode);

    if (lightIndex >= 0) {

      /* its a dark theme */

      return Theme.buildLightTheme(lightModes[lightIndex]);
    }

    /* no such theme */

    return null;
  }

  /** build the given dark theme */
  public static buildDarkTheme(mode: DarkThemeMode): Theme {

    switch (mode) {

      case DarkThemeMode.DARK:  return Theme.dark();
      case DarkThemeMode.DARK1: return Theme.dark1();
      case DarkThemeMode.DARK2: return Theme.dark2();
      case DarkThemeMode.DARK3: return Theme.dark3();
      case DarkThemeMode.DARK4: return Theme.dark4();
      case DarkThemeMode.DARK5: return Theme.dark5();

    }

    /* default when we don't know what it is */

    return Theme.dark();
  }

  /** build the given light theme */
  public static buildLightTheme(mode: LightThemeMode): Theme {

    /* default when we don't know what it is */

    switch (mode) {

      case LightThemeMode.LIGHT:  return Theme.light();
      case LightThemeMode.LIGHT1: return Theme.light1();
      case LightThemeMode.LIGHT2: return Theme.light2();
      case LightThemeMode.LIGHT3: return Theme.light3();
      case LightThemeMode.LIGHT4: return Theme.light4();
      case LightThemeMode.LIGHT5: return Theme.light5();

    }

    return Theme.light();
  }

  /** build and return a light theme */
  public static light(): Theme {

    return new Theme({
      lightMode:                LightThemeMode.LIGHT,
      darkMode:                 DarkThemeMode.NONE,
      wrapper_background_color: '#d5eeff',
      wrapper_foreground_color: '#005689',
      canvas_background_color:  '#d5eeff',
      canvas_foreground_color:  '#005689',
      grid_color:               '#525252',
      spike_alpha:              1.0,
      spike_color:              `#ff895d`,
      spike_width:              .5,
      toolbar_height:           24

    });
  }

  /** build and return a light theme */
  public static light1(): Theme {

    /*
     * https://coolors.co/palette/f55d3e-878e88-f7cb15-ffffff-76bed0
     *
     * F55D3E
     * 878E88
     * F7CB15
     * FFFFFF
     * 76BED0
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.LIGHT1,
      darkMode:                 DarkThemeMode.NONE,
      wrapper_background_color: '#FFFFFF',
      wrapper_foreground_color: '#878E88',
      canvas_background_color:  '#FFFFFF',
      canvas_foreground_color:  '#878E88',
      grid_color:               '#525252',
      spike_alpha:              1.0,
      spike_color:              `#F7CB15`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

  /** build and return a light theme */
  public static light2(): Theme {

    /*
     * https://coolors.co/palette/d3d4d9-4b88a2-bb0a21-252627-fff9fb
     *
     * D3D4D9
     * 4B88A2
     * BB0A21
     * 252627
     * FFF9FB
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.LIGHT2,
      darkMode:                 DarkThemeMode.NONE,
      wrapper_background_color: '#FFF9FB',
      wrapper_foreground_color: '#878E88',
      canvas_background_color:  '#FFF9FB',
      canvas_foreground_color:  '#878E88',
      grid_color:               '#525252',
      spike_alpha:              1.0,
      spike_color:              `#BB0A21`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

  /** build and return a light theme */
  public static light3(): Theme {

    /*
     * https://coolors.co/palette/e7ecef-274c77-6096ba-a3cef1-8b8c89
     *
     * E7ECEF
     * 274C77
     * 6096BA
     * A3CEF1
     * 8B8C89
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.LIGHT3,
      darkMode:                 DarkThemeMode.NONE,
      wrapper_background_color: '#E7ECEF',
      wrapper_foreground_color: '#274C77',
      canvas_background_color:  '#E7ECEF',
      canvas_foreground_color:  '#274C77',
      grid_color:               '#525252',
      spike_alpha:              1.0,
      spike_color:              `#274C77`,
      spike_width:              .5,
      toolbar_height:           24

    });
  }

  /** build and return a light theme */
  public static light4(): Theme {

    /*
     * https://coolors.co/palette/26547c-ef476f-ffd166-06d6a0-fffcf9
     *
     * 26547C
     * EF476F
     * FFD166
     * 06D6A0
     * FFFCF9
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.LIGHT4,
      darkMode:                 DarkThemeMode.NONE,
      wrapper_background_color: '#FFFCF9',
      wrapper_foreground_color: '#26547C',
      canvas_background_color:  '#FFFCF9',
      canvas_foreground_color:  '#26547C',
      grid_color:               '#525252',
      spike_alpha:              1.0,
      spike_color:              `#FFD166`,
      spike_width:              .5,
      toolbar_height:           24

    });
  }

  /** build and return a light theme */
  public static light5(): Theme {

    /*
     * https://coolors.co/palette/2f6690-3a7ca5-d9dcd6-16425b-81c3d7
     *
     * 2F6690
     * 3A7CA5
     * D9DCD6
     * 16425B
     * 81C3D7
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.LIGHT5,
      darkMode:                 DarkThemeMode.NONE,
      wrapper_background_color: '#D9DCD6',
      wrapper_foreground_color: '#16425B',
      canvas_background_color:  '#D9DCD6',
      canvas_foreground_color:  '#16425B',
      grid_color:               '#525252',
      spike_alpha:              1.0,
      spike_color:              `#2F6690`,
      spike_width:              .5,
      toolbar_height:           24

    });
  }

  /** build and return a dark theme */
  public static dark(): Theme {

    return new Theme({
      lightMode:                LightThemeMode.NONE,
      darkMode:                 DarkThemeMode.DARK,
      wrapper_background_color: '#005689',
      wrapper_foreground_color: '#d5eeff',
      canvas_background_color:  '#005689',
      canvas_foreground_color:  '#d5eeff',
      grid_color:               '#404040',
      spike_alpha:              1.0,
      spike_color:              `#ff895d`,
      spike_width:              .5,
      toolbar_height:           24
    });

  }

  /** build and return a dark theme */
  public static dark1(): Theme {

    /*
     * https://coolors.co/palette/f55d3e-878e88-f7cb15-ffffff-76bed0
     *
     * F55D3E
     * 878E88
     * F7CB15
     * FFFFFF
     * 76BED0
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.NONE,
      darkMode:                 DarkThemeMode.DARK1,
      wrapper_background_color: '#878E88',
      wrapper_foreground_color: '#FFFFFF',
      canvas_background_color:  '#878E88',
      canvas_foreground_color:  '#FFFFFF',
      grid_color:               '#404040',
      spike_alpha:              1.0,
      spike_color:              `#d4d4d4`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

  /** build and return a dark theme */
  public static dark2(): Theme {

    /*
     * https://coolors.co/palette/d3d4d9-4b88a2-bb0a21-252627-fff9fb
     *
     * D3D4D9
     * 4B88A2
     * BB0A21
     * 252627
     * FFF9FB
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.NONE,
      darkMode:                 DarkThemeMode.DARK2,
      wrapper_background_color: '#252627',
      wrapper_foreground_color: '#D3D4D9',
      canvas_background_color:  '#252627',
      canvas_foreground_color:  '#D3D4D9',
      grid_color:               '#6e6e6e',
      spike_alpha:              1.0,
      spike_color:              `#D3D4D9`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

  /** build and return a dark theme */
  public static dark3(): Theme {

    /*
     * https://coolors.co/palette/e7ecef-274c77-6096ba-a3cef1-8b8c89
     *
     * E7ECEF
     * 274C77
     * 6096BA
     * A3CEF1
     * 8B8C89
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.NONE,
      darkMode:                 DarkThemeMode.DARK3,
      wrapper_background_color: '#274C77',
      wrapper_foreground_color: '#E7ECEF',
      canvas_background_color:  '#274C77',
      canvas_foreground_color:  '#E7ECEF',
      grid_color:               '#404040',
      spike_alpha:              1.0,
      spike_color:              `#E7ECEF`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

  /** build and return a dark theme */
  public static dark4(): Theme {

    /*
     * https://coolors.co/palette/26547c-ef476f-ffd166-06d6a0-fffcf9
     *
     * 26547C
     * EF476F
     * FFD166
     * 06D6A0
     * FFFCF9
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.NONE,
      darkMode:                 DarkThemeMode.DARK4,
      wrapper_background_color: '#26547C',
      wrapper_foreground_color: '#FFFCF9',
      canvas_background_color:  '#26547C',
      canvas_foreground_color:  '#FFFCF9',
      grid_color:               '#404040',
      spike_alpha:              1.0,
      spike_color:              `#FFFCF9`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

  /** build and return a dark theme */
  public static dark5(): Theme {

    /*
     * https://coolors.co/palette/2f6690-3a7ca5-d9dcd6-16425b-81c3d7
     *
     * 2F6690
     * 3A7CA5
     * D9DCD6
     * 16425B
     * 81C3D7
     *
     */
    return new Theme({
      lightMode:                LightThemeMode.NONE,
      darkMode:                 DarkThemeMode.DARK5,
      wrapper_background_color: '#16425B',
      wrapper_foreground_color: '#D9DCD6',
      canvas_background_color:  '#16425B',
      canvas_foreground_color:  '#D9DCD6D9DCD6',
      grid_color:               '#404040',
      spike_alpha:              1.0,
      spike_color:              `#D9DCD6`,
      spike_width:              .5,
      toolbar_height:           24
    });
  }

}
